import type { BoardSize, ProblemBoardAnnotation, StoneColor } from '@wx-weiqi/contracts';

import { boardCoordinate, stoneRadius } from '../domain/board-geometry';

export function ProblemAnnotationLayer({
  size,
  stones,
  annotations,
}: {
  readonly size: BoardSize;
  readonly stones: readonly (StoneColor | null)[];
  readonly annotations: readonly ProblemBoardAnnotation[];
}) {
  const radius = stoneRadius(size);
  return (
    <g className="problem-initial-annotations" pointerEvents="none" aria-label="题目初始标记">
      {annotations.map((mark, index) => {
        if (mark.point.x >= size || mark.point.y >= size) return null;
        const x = boardCoordinate(size, mark.point.x);
        const y = boardCoordinate(size, mark.point.y);
        const stone = stones[mark.point.y * size + mark.point.x] ?? null;
        const color = stone === 'BLACK' ? '#fff8e7' : '#173e47';
        const r = radius * 0.55;
        if (mark.kind === 'LABEL') {
          const count = Array.from(mark.text).length;
          const fontSize = radius * (count > 2 ? 0.78 : 1.05);
          return (
            <g key={index} data-sgf-mark="LABEL">
              <title>{mark.text}</title>
              {stone === null ? (
                <rect
                  x={x - radius * 0.84}
                  y={y - radius * 0.74}
                  width={radius * 1.68}
                  height={radius * 1.48}
                  rx={radius * 0.15}
                  fill="#d4a657"
                />
              ) : null}
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="central"
                fill={color}
                fontSize={fontSize}
                fontWeight={750}
                {...(count > 2
                  ? { textLength: radius * 1.55, lengthAdjust: 'spacingAndGlyphs' as const }
                  : {})}
              >
                {mark.text}
              </text>
            </g>
          );
        }
        if (mark.kind === 'ARROW' || mark.kind === 'LINE') {
          if (mark.to.x >= size || mark.to.y >= size) return null;
          const toX = boardCoordinate(size, mark.to.x);
          const toY = boardCoordinate(size, mark.to.y);
          const angle = Math.atan2(toY - y, toX - x);
          const head = radius * 0.8;
          return (
            <g
              key={index}
              data-sgf-mark={mark.kind}
              fill="none"
              stroke="#b03227"
              strokeWidth={Math.max(2, radius * 0.14)}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1={x} y1={y} x2={toX} y2={toY} />
              {mark.kind === 'ARROW' ? (
                <path
                  d={`M ${toX - head * Math.cos(angle - 0.5)} ${toY - head * Math.sin(angle - 0.5)} L ${toX} ${toY} L ${toX - head * Math.cos(angle + 0.5)} ${toY - head * Math.sin(angle + 0.5)}`}
                />
              ) : null}
            </g>
          );
        }
        return (
          <g
            key={index}
            data-sgf-mark={mark.kind}
            fill="none"
            stroke={color}
            strokeWidth={Math.max(2, radius * 0.13)}
            strokeLinejoin="round"
          >
            {mark.kind === 'CIRCLE' ? <circle cx={x} cy={y} r={r} /> : null}
            {mark.kind === 'TRIANGLE' ? (
              <path
                d={`M ${x} ${y - r * 1.15} L ${x + r} ${y + r * 0.8} L ${x - r} ${y + r * 0.8} Z`}
              />
            ) : null}
            {mark.kind === 'SQUARE' || mark.kind === 'SELECT' ? (
              <rect
                x={x - r}
                y={y - r}
                width={r * 2}
                height={r * 2}
                fill={mark.kind === 'SELECT' ? color : 'none'}
                fillOpacity={mark.kind === 'SELECT' ? 0.35 : 1}
              />
            ) : null}
            {mark.kind === 'CROSS' ? (
              <path
                d={`M ${x - r} ${y - r} L ${x + r} ${y + r} M ${x + r} ${y - r} L ${x - r} ${y + r}`}
              />
            ) : null}
          </g>
        );
      })}
    </g>
  );
}
